import React from "react";
import { ItemizedSummary } from "../ItemizedSummary";
import { BillingItemizedSummaryLineItem } from "./types";

type ExpandableProps = {
  items: BillingItemizedSummaryLineItem[];
  primaryUnit?: "monthly" | "hourly";
  UNSAFE_listMaxHeight?: number;
  UNSAFE_borderTop?: boolean;

  expandable: true;
  expanded: boolean;
  onToggleExpanded(): void;
};

type NonExpandableProps = {
  items: BillingItemizedSummaryLineItem[];
  primaryUnit?: "monthly" | "hourly";
  UNSAFE_listMaxHeight?: number;
  UNSAFE_borderTop?: boolean;

  expandable?: false;
  expanded?: never;
  onToggleExpanded?: never;
};

type Props = ExpandableProps | NonExpandableProps;

const HOURS_IN_A_28_DAY_MONTH = 672;
const HOURS_IN_A_31_DAY_MONTH = 744;

const mapLineItemTotal = (total: number, primaryUnit: "monthly" | "hourly") => {
  if (primaryUnit === "monthly") {
    return `$${total.toFixed(2)}/mo`;
  } else {
    return `$${total.toFixed(3)}/hr`;
  }
};

export const BillingItemizedSummary: React.FC<Props> = (props) => {
  const { items, primaryUnit = "monthly", UNSAFE_borderTop = false, UNSAFE_listMaxHeight } = props;

  const totals = React.useMemo(() => {
    const totalCost = items.reduce((prev, next) => prev + next.total, 0);

    if (primaryUnit === "monthly") {
      return {
        title: "Total cost",
        amounts: [
          {
            type: "primary" as const,
            amount: `$${totalCost.toFixed(2)}`,
            unit: "month",
          },
          {
            type: "secondary" as const,
            amount: `$${(totalCost / HOURS_IN_A_28_DAY_MONTH).toFixed(3)}`,
            unit: "hour",
          },
        ],
      };
    } else {
      return {
        title: "Total cost",
        amounts: [
          {
            type: "primary" as const,
            amount: `$${totalCost.toFixed(3)}`,
            unit: "hour",
          },
          {
            type: "secondary" as const,
            amount: `$${(totalCost * HOURS_IN_A_31_DAY_MONTH).toFixed(2)}`,
            unit: "month",
          },
        ],
      };
    }
  }, [items, primaryUnit]);

  const itemsForSummary = items.map((item) => ({
    title: item.title,
    subtitle: item.subtitle,
    icon: item.icon,
    total: mapLineItemTotal(item.total, primaryUnit),
    notes: item.notes ?? [],
  }));

  if (props.expandable) {
    return (
      <ItemizedSummary
        expandable
        expanded={props.expanded}
        onToggleExpanded={props.onToggleExpanded}
        items={itemsForSummary}
        totals={totals}
        UNSAFE_maxHeight={UNSAFE_listMaxHeight}
        UNSAFE_borderTop={UNSAFE_borderTop}
      />
    );
  } else {
    return (
      <ItemizedSummary
        items={itemsForSummary}
        totals={totals}
        UNSAFE_maxHeight={UNSAFE_listMaxHeight}
        UNSAFE_borderTop={UNSAFE_borderTop}
      />
    );
  }
};

BillingItemizedSummary.displayName = "BillingItemizedSummary";
